import {
  combineReducers,
  configureStore,
  createListenerMiddleware
} from '@reduxjs/toolkit';
import isLoadingReducer from 'common/components/loading/isLoadingSlice';
import availableAttributionModelsReducer from 'features/attribute-model/availableAttributionModelsSlice';
import availableAttributionCompareModelsReducer from 'features/attribute-compare-model/availableAttributionCompareModelsSlice';
import selectedSiteReducer from 'features/topbar/selectedSiteSlice';
import dateRangeReducer from 'features/date-range-selection/dateRangeSlice';
import chartOptionsReducer from 'features/charts/chartOptionsSlice';
import paginationOptionsReducer from 'features/pagination/paginationOptionsSlice';
import searchBarReducer from 'features/search-bar/searchBarSlice';
import authReducer from 'features/auth/authenticationSlice';
import { authApi } from 'app/apis/authApi';
import { queryApi } from 'app/apis/queryApi';
import sidebarTogglingReducer from 'features/sidebar/sidebarTogglingSlice';
import sidebarReducer from 'features/sidebar/sidebarSlice';
import pocToggleReducer from 'features/poc/poc-toggle/pocToggleSlice';
import helpTileReducer from 'features/dashboard/helpTileSlice';
import rowSelectionReducer from 'features/row-selection/rowSelectionSlice';
import modalSliceReducer from 'features/modal/modalSlice';
import accordionSliceReducer from 'common/components/accordion/accordionSlice';
import dataViewSelectionReducer from 'features/data-view-selection/dataViewSelectionSlice';
import googleStaticMapReducer from 'features/maps/google-static-maps/googleStaticMapSlice';
import settingsReducer from 'features/settings/settingsSlice';
import {
  persistStore,
  persistReducer,
  FLUSH,
  REHYDRATE,
  PAUSE,
  PERSIST,
  PURGE,
  REGISTER,
  PersistConfig
} from 'redux-persist';
import storage from 'redux-persist-indexeddb-storage';
import { commandApi } from './apis/commandApi';
import blurReducer from 'features/blur/blurToggleSlice';
import { publicCommandApi } from './apis/publicCommandApi';
import toastReducer from 'features/toast/toast.slice';
import { uploadReducer } from 'features/upload';
import mmmHeaderControlsReducer from 'features/marketing-mix-modelling/header-controls/header-controls.slice';
import reportsTableReducer from 'features/reports/reports-table.slice';
import webhooksTableReducer from 'features/webhooks/webhooks-table/webhooks-table.slice';
import pathsTableReducer from 'features/conversions/paths/paths.slice';
import exclusionsTableReducer from 'features/exclusions/exclusions-table/exclusions-table.slice';
import internalSettingsReducer from 'features/internal/internal-settings.slice';
import segmentControlsReducer from 'features/segment-controls/segment-controls.slice';
import advancedReportsTableReducer from 'features/advanced-reports/table/advanced-reports-table.slice';
import rowPendingDeleteSliceReducer from 'features/row-deletion/rowPendingDeletionSlice';
import conversionInfluenceInsightsReducer from 'features/insights/conversion-influence/conversion-influence-table.slice';
import autoMergeLevel2 from 'redux-persist/es/stateReconciler/autoMergeLevel2';
import { advancerReportsSiteIdChangedHandler } from 'features/advanced-reports/advanced-reports.middleware';
import { toastMiddleware } from 'features/toast/toast.listenermiddleware';
import {
  opportunityRowPendingDeletionListenerMiddleware,
  revenueRowPendingDeletionListenerMiddleware
} from 'features/row-deletion/rowPendingDeletion.listenermiddleware';
import segmentationConfigReducer from 'features/segmentation-config/segmentation-config.slice';
import clientIntegrationMetaReducer from 'features/client-integrations/clientIntegration-meta-slice';
import platformConfigReducer from 'features/client-integrations/integration-setup/platform-config/platform-config.slice';
import { accountIdDeleteOnModelConfirmMiddleware } from 'features/client-integrations/integration-setup/confirm-account-id-delete.middleware';
import { mmmControlReducer } from 'features/marketing-mix-modelling/budget-scenario-planner/mmm-controls/mmm-control.slice';
import { campaignMmmControlReducer } from 'features/marketing-mix-modelling/campaign-budget-scenario-planner/campaign-mmm-controls/campaign-mmm-control.slice';
import { editBudgetScenarioReducer } from 'features/marketing-mix-modelling/budget-scenario-planner/edit-budget-scenario.slice';
import { budgetScenarioListener } from 'features/marketing-mix-modelling/budget-scenario-planner/edit-budget-scenario.listener';
import { budgetScenarioSaveModalReducer } from 'features/marketing-mix-modelling/budget-scenario-planner/save-modal/budget-scenario-save-modal.slice';

const rootReducer = combineReducers({
  [commandApi.reducerPath]: commandApi.reducer,
  [publicCommandApi.reducerPath]: publicCommandApi.reducer,
  [authApi.reducerPath]: authApi.reducer,
  [queryApi.reducerPath]: queryApi.reducer,
  blur: blurReducer,
  dateRange: dateRangeReducer,
  isLoading: isLoadingReducer,
  selectedSite: selectedSiteReducer,
  auth: authReducer,
  chartOptions: chartOptionsReducer,
  paginationOptions: paginationOptionsReducer,
  searchBar: searchBarReducer,
  sidebar: sidebarTogglingReducer,
  sidebarv2: sidebarReducer,
  pocToggle: pocToggleReducer,
  helpTile: helpTileReducer,
  rowSelection: rowSelectionReducer,
  modal: modalSliceReducer,
  accordions: accordionSliceReducer,
  dataViewSelections: dataViewSelectionReducer,
  googleStaticMap: googleStaticMapReducer,
  settings: settingsReducer,
  toast: toastReducer,
  upload: uploadReducer,
  mmmHeaderControls: mmmHeaderControlsReducer,
  reportsTable: reportsTableReducer,
  availableAttributionModels: availableAttributionModelsReducer,
  availableAttributionCompareModels: availableAttributionCompareModelsReducer,
  webhooksTable: webhooksTableReducer,
  exclusionsTable: exclusionsTableReducer,
  pathsTable: pathsTableReducer,
  internalSettings: internalSettingsReducer,
  segmentControls: segmentControlsReducer,
  advancedReportsTable: advancedReportsTableReducer,
  conversionInfluenceInsightsTable: conversionInfluenceInsightsReducer,
  rowsPendingDelete: rowPendingDeleteSliceReducer,
  segmentationConfig: segmentationConfigReducer,
  clientIntegrationMeta: clientIntegrationMetaReducer,
  platformConfig: platformConfigReducer,
  mmmControl: mmmControlReducer,
  campaignMmmControl: campaignMmmControlReducer,
  editBudgetScenario: editBudgetScenarioReducer,
  budgetScenarioSaveModal: budgetScenarioSaveModalReducer
});

const persistConfig: PersistConfig<any> = {
  key: 'root',
  stateReconciler: autoMergeLevel2,
  version: 2,
  storage: storage('appState'),
  blacklist: [
    authApi.reducerPath,
    queryApi.reducerPath,
    commandApi.reducerPath,
    publicCommandApi.reducerPath,
    'helpTile',
    'modal',
    'toast',
    'upload',
    'sidebar',
    'sidebarv2',
    'mmmHeaderControls',
    'webhooksTable',
    'segmentControls',
    'advancedReportsTable',
    'availableAttributionModels',
    'availableAttributionCompareModels',
    'clientIntegrationMeta',
    'platformConfig',
    'budgetScenarioSaveModal'
  ],
  timeout: undefined
};

const persistedReducer = persistReducer(persistConfig, rootReducer);

// listenerMiddleware used for inline listeners in components i.e. dispatch(addListener({ actionCreator: confirmModal, effect: ... }))
const genericListenerMiddleware = createListenerMiddleware();

export const store = configureStore({
  reducer: persistedReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER]
      }
    })
      .prepend([
        genericListenerMiddleware.middleware,
        toastMiddleware.middleware,
        revenueRowPendingDeletionListenerMiddleware.middleware,
        opportunityRowPendingDeletionListenerMiddleware.middleware,
        accountIdDeleteOnModelConfirmMiddleware.middleware,
        budgetScenarioListener
      ]) // Must be prepended. See https://redux-toolkit.js.org/api/createListenerMiddleware#basic-usage
      .concat(
        advancerReportsSiteIdChangedHandler,
        authApi.middleware,
        queryApi.middleware,
        commandApi.middleware,
        publicCommandApi.middleware
      ),
  devTools: process.env.NODE_ENV !== 'production'
});

export const persistor = persistStore(store);

export type RootState = ReturnType<typeof rootReducer>;
export type AppDispatch = typeof store.dispatch;
